import React, { useMemo } from 'react';
import { Input, Select } from 'antd';

const PROVIDERS = [
  { id: 'ollama', icon: '🦙', label: 'Ollama', sub: 'Local · Private' },
  { id: 'anthropic', icon: '☁️', label: 'Claude', sub: 'Anthropic' },
  { id: 'openai', icon: '⚡', label: 'OpenAI', sub: 'or LM Studio' },
];

const ANTHROPIC_MODELS = [
  { value: 'claude-opus-4-6', label: 'claude-opus-4-6 (best)' },
  { value: 'claude-sonnet-4-6', label: 'claude-sonnet-4-6 (faster)' },
];

function statusDotClass(kind) {
  if (kind === 'online') return 'fipb-status-dot fipb-dot-green';
  if (kind === 'warn') return 'fipb-status-dot fipb-dot-amber';
  if (kind === 'error') return 'fipb-status-dot fipb-dot-red';
  return 'fipb-status-dot fipb-dot-gray';
}

function providerHeaderStatus(llmStatus, provider) {
  if (!llmStatus) return 'offline';
  if (provider === 'ollama') {
    return llmStatus.ollama_running ? 'online' : 'error';
  }
  if (provider === 'anthropic') {
    return llmStatus.env_anthropic_set ? 'online' : 'warn';
  }
  if (provider === 'openai') {
    return llmStatus.env_openai_set ? 'online' : 'warn';
  }
  return 'offline';
}

export default function LlmProviderPanel({
  provider,
  onProviderChange,
  model,
  onModelChange,
  anthropicModel,
  onAnthropicModelChange,
  openaiModel,
  onOpenaiModelChange,
  openaiBaseUrl,
  onOpenaiBaseUrlChange,
  llmStatus,
}) {
  const headerStatus = providerHeaderStatus(llmStatus, provider);

  const modelChips = useMemo(() => {
    if (!llmStatus) return [];
    const pulled = llmStatus.pulled_models || [];
    const recs = (llmStatus.recommendations || []).map((r) => r.model);
    return [...new Set([...pulled, ...recs])];
  }, [llmStatus]);

  const recommendations = llmStatus?.recommendations || [];

  const ollamaStatusText = (() => {
    if (!llmStatus) return 'Checking Ollama…';
    if (llmStatus.ollama_running) {
      const n = (llmStatus.pulled_models || []).length;
      return `Running · ${n} model(s) available`;
    }
    return 'Ollama not running — run: ollama serve';
  })();

  const ollamaDot = llmStatus?.ollama_running ? 'online' : llmStatus ? 'error' : 'offline';

  const anthropicStatusText = (() => {
    if (!llmStatus) return 'Checking API…';
    return llmStatus.env_anthropic_set
      ? 'ANTHROPIC_API_KEY is set ✓'
      : 'Set ANTHROPIC_API_KEY in server env';
  })();

  const anthropicDot = llmStatus?.env_anthropic_set ? 'online' : llmStatus ? 'warn' : 'offline';

  return (
    <div className="fipb-llm-section">
      <div className="fipb-llm-section-title">
        <span>🤖</span>
        <span>LLM provider</span>
        <span className={statusDotClass(headerStatus)} style={{ marginLeft: 'auto' }} />
      </div>

      <div className="fipb-provider-grid">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`fipb-provider-btn${provider === p.id ? ' active' : ''}`}
            onClick={() => onProviderChange(p.id)}
          >
            <span className="fipb-pb-icon">{p.icon}</span>
            <span className="fipb-pb-label">{p.label}</span>
            <span className="fipb-pb-sub">{p.sub}</span>
          </button>
        ))}
      </div>

      {provider === 'ollama' && (
        <div className="fipb-provider-panel">
          <div className="fipb-llm-status-row">
            <span className={statusDotClass(ollamaDot)} />
            <span>{ollamaStatusText}</span>
          </div>
          <div className="fipb-model-label">Select model</div>
          <div className="fipb-model-chips">
            {modelChips.length === 0 ? (
              <span className="fipb-model-empty">No models listed — check API connection</span>
            ) : (
              modelChips.map((m) => {
                const isPulled = (llmStatus?.pulled_models || []).includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    className={`fipb-model-chip${model === m ? ' active' : ''}`}
                    onClick={() => onModelChange(m)}
                    title={isPulled ? 'Available locally' : 'Not yet pulled'}
                  >
                    {m}
                    {isPulled ? ' ✓' : ' ↓'}
                  </button>
                );
              })
            )}
          </div>
          {recommendations.length > 0 && (
            <>
              <div className="fipb-rec-heading">Recommended for this task:</div>
              <div className="fipb-rec-grid">
                {recommendations.slice(0, 3).map((rec, i) => (
                  <button
                    key={rec.model}
                    type="button"
                    className={`fipb-rec-card${i === 0 ? ' recommended' : ''}`}
                    onClick={() => onModelChange(rec.model)}
                  >
                    <div className="fipb-rc-model">
                      {rec.model}
                      {i === 0 ? ' ⭐' : ''}
                    </div>
                    <div className="fipb-rc-why">{rec.why}</div>
                    <div className="fipb-rc-meta">
                      <span>VRAM: {rec.vram_gb}GB</span>
                      <span>Speed: {rec.speed}</span>
                      <span>Context: {rec.context_k}k</span>
                      <span className={`fipb-rc-badge ${rec.available ? 'fipb-rc-avail' : 'fipb-rc-pull'}`}>
                        {rec.available ? 'Ready' : 'Pull needed'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {provider === 'anthropic' && (
        <div className="fipb-provider-panel">
          <label className="fipb-field fipb-field--compact">
            <span>Claude model</span>
            <Select
              value={anthropicModel}
              onChange={onAnthropicModelChange}
              options={ANTHROPIC_MODELS}
              style={{ width: '100%' }}
            />
          </label>
          <div className="fipb-llm-status-row">
            <span className={statusDotClass(anthropicDot)} />
            <span>{anthropicStatusText}</span>
          </div>
        </div>
      )}

      {provider === 'openai' && (
        <div className="fipb-provider-panel">
          <label className="fipb-field fipb-field--compact">
            <span>Model name</span>
            <Input
              value={openaiModel}
              onChange={(e) => onOpenaiModelChange(e.target.value)}
              placeholder="gpt-4o or local model"
            />
          </label>
          <label className="fipb-field fipb-field--compact">
            <span>Base URL (LM Studio etc.)</span>
            <Input
              value={openaiBaseUrl}
              onChange={(e) => onOpenaiBaseUrlChange(e.target.value)}
              placeholder="http://localhost:1234/v1"
            />
          </label>
          <div className="fipb-llm-status-row">
            <span className={statusDotClass(llmStatus?.env_openai_set ? 'online' : 'warn')} />
            <span>Set OPENAI_API_KEY in server env</span>
          </div>
        </div>
      )}

      {llmStatus?.active_provider && (
        <div className="fipb-active-llm">
          Active: <strong>{llmStatus.active_provider}</strong> /{' '}
          <code>{llmStatus.active_model}</code>
        </div>
      )}
    </div>
  );
}
